import React, { useEffect, useRef, useState } from "react";
import DynamicChart from '../../components/charts/dynamicChart';
import SummaryTable from '../../components/sections/summaryTable';
import FiltersSection from '../../components/sections/filters';
import MainCards from '../../components/sections/thd/mainCards';
import { useDispatch, useSelector } from "react-redux";
import { changeDimension, changeFilter, getDirectCampaignsData } from '../../stateManagement/actions/report';
import Loader from '../../components/layout/loader';
import { chartPreparation } from "../../services/dataHandling";
import { ADMIN_ACCESS_MASK, PARTITION_THD } from "../../utils/constants";
import { useAuth } from "../../hooks/useAuth";

const dimensions = [
    { label: 'Order name', value: 'orderName', key: '1' },
    { label: 'Platform', value: 'provider', key: '2' },
    { label: 'Publisher', value: 'publisher', key: '3' },
    { label: 'Website', value: 'website', key: '4' },
    { label: 'Advertiser', value: 'orderAdvertiser', key: '5' }
];

const DirectPage = () => {
    const dispatch = useDispatch();

    const [fullReport, setFullReport] = useState([]);
    const [chartData, setChartData] = useState({ topValues: [], data: [] });
    const [dataReloaded, setDataReloaded] = useState(false);
    const [selectedCard, setSelectedCard] = useState('revenue');

    /**
     * Retinem daca pagina a fost deja initializata.
     * Resetam dimension/filterBy doar la prima intrare in pagina.
     */
    const initializedPageRef = useRef(null);

    /**
     * Evita request-uri identice consecutive.
     * Util mai ales in development, cu React.StrictMode.
     */
    const lastFetchKeyRef = useRef(null);

    const { user } = useAuth();

    const reportData = useSelector((state) => state.reportReducer);
    const {loading, period, dimension, filterBy, publisherView} = reportData;

    const DEFAULT_DIMENSION = 'orderName';

    const GREETINGS_FOR =
        publisherView === ADMIN_ACCESS_MASK
            ? user && user.subAccount
                ? 'ThematicDigital'
                : 'Gemius'
            : publisherView;

    /**
     * Reset + fetch controlat.
     * - daca dimension/filterBy trebuie resetate, facem reset si iesim din efect
     * - request-ul pleaca abia dupa ce Redux are valorile corecte
     */
    useEffect(() => {
        /**
         * Daca publisherView este setat global in alta parte,
         * evitam request-ul pana cand avem valoarea disponibila.
         */
        if (!publisherView) {
            return;
        }

        const pageKey = `${PARTITION_THD}-direct-campaigns-${DEFAULT_DIMENSION}`;
        const isFirstRunForPage = initializedPageRef.current !== pageKey;

        const dimensionIsAllowed = dimensions.some((item) => item.value === dimension);

        const needsDimensionReset = !dimensionIsAllowed || (isFirstRunForPage && dimension !== DEFAULT_DIMENSION);

        const needsFilterReset = isFirstRunForPage && filterBy !== '';

        if (needsFilterReset) {
            dispatch(changeFilter(''));
        }

        if (needsDimensionReset) {
            dispatch(changeDimension(DEFAULT_DIMENSION));
        }

        /**
         * Important:
         * daca am facut reset, nu facem request in acelasi ciclu.
         * Asteptam urmatorul render, cu dimension/filterBy corecte in Redux.
         */
        if (needsFilterReset || needsDimensionReset) {
            return;
        }

        initializedPageRef.current = pageKey;

        const fetchKey = JSON.stringify({period, dimension, filterBy, publisherView, partition: PARTITION_THD});

        if (lastFetchKeyRef.current === fetchKey) {
            return;
        }

        lastFetchKeyRef.current = fetchKey;

        setDataReloaded(false);

        dispatch(getDirectCampaignsData({period, dimension, filterBy, publisherView, partition: PARTITION_THD}, setFullReport, setDataReloaded));
    }, [dispatch, period, dimension, filterBy, publisherView, DEFAULT_DIMENSION]);

    /**
     * Pregatim datele pentru chart dupa ce API-ul a incarcat/reincarcat datele.
     */
    useEffect(() => {
        if (dataReloaded) {
            const computedData = chartPreparation(fullReport, dimension, selectedCard, period);
            setChartData(computedData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullReport, dataReloaded, selectedCard]);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <h2>{`Direct campaigns for ${GREETINGS_FOR} account`}</h2>
            <FiltersSection
                dimensions={dimensions}
                defaultSelection={DEFAULT_DIMENSION}
            />
            <div className="main-section">
                <MainCards
                    partition={PARTITION_THD}
                    reportScope="direct"
                    fullData={fullReport}
                    trigger={setSelectedCard}
                    currentState={selectedCard}
                    limited
                />
                <DynamicChart
                    fullData={chartData}
                    currentState={selectedCard}
                />
            </div>
            <SummaryTable
                partition={PARTITION_THD}
                reportScope="direct"
                fullData={fullReport}
                currentState={selectedCard}
            />
        </>
    );
};

export default DirectPage;